import React, { useState } from 'react';
import { Drawer, Modal, Button, Input, Select, Table, message } from 'antd';
import { t } from '../../../../i18n';
import { checkDeliveryDriverAccounts, CheckDeliveryDriverAccountsResult } from '../graphql/queries/checkDeliveryDriverAccounts';
import { setDriverBlacklist } from '../graphql/mutations/setDriverBlacklist';
import { showError } from 'utils/exceptionUtils';

const { TextArea } = Input;

interface DriverBlacklistModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const BLACKLIST_ERROR_I18N: Record<string, string> = {
    BLACKLIST_ERR_NO_PERMISSION: 'delivery.personnel.message.blacklistErrNoPermission',
    BLACKLIST_ERR_PHONE_LIST_EMPTY: 'delivery.personnel.message.blacklistErrPhoneListEmpty',
    BLACKLIST_ERR_NO_VALID_PHONES: 'delivery.personnel.message.blacklistErrNoValidPhones',
    BLACKLIST_ERR_GENERAL: 'delivery.personnel.message.blacklistErrGeneral',
};

const BAN_REASON_OPTIONS = [
    { value: 1, labelKey: 'delivery.personnel.label.banReason1' },
    { value: 2, labelKey: 'delivery.personnel.label.banReason2' },
    { value: 3, labelKey: 'delivery.personnel.label.banReason3' },
    { value: 4, labelKey: 'delivery.personnel.label.banReason4' },
    { value: 5, labelKey: 'delivery.personnel.label.banReason5' },
];

const BAN_TYPE_OPTIONS = [
    { value: 1, labelKey: 'delivery.personnel.label.permanentBan' },
    { value: 2, labelKey: 'delivery.personnel.label.temporaryBan' },
];

const BAN_PERIOD_OPTIONS = [
    { value: 1, labelKey: 'delivery.personnel.label.banPeriod1' },
    { value: 2, labelKey: 'delivery.personnel.label.banPeriod2' },
    { value: 3, labelKey: 'delivery.personnel.label.banPeriod3' },
    { value: 4, labelKey: 'delivery.personnel.label.banPeriod4' },
    { value: 5, labelKey: 'delivery.personnel.label.banPeriod5' },
];

const DriverBlacklistModal: React.FC<DriverBlacklistModalProps> = ({ visible, onCancel, onSuccess }) => {
    const [phoneInput, setPhoneInput] = useState('');
    const [checkLoading, setCheckLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [checkResult, setCheckResult] = useState<CheckDeliveryDriverAccountsResult | null>(null);
    const [banReason, setBanReason] = useState<number | undefined>(undefined);
    const [banType, setBanType] = useState<number | undefined>(undefined);
    const [banPeriod, setBanPeriod] = useState<number | undefined>(undefined);

    const handleClose = () => {
        setPhoneInput('');
        setCheckResult(null);
        setBanReason(undefined);
        setBanType(undefined);
        setBanPeriod(undefined);
        onCancel();
    };

    const handleCheckPhones = async () => {
        const raw = phoneInput.trim();
        if (!raw) {
            message.warning(t('delivery.personnel.message.inputAccountRequired'));
            return;
        }
        const phones = raw.split(',').map(p => p.trim()).filter(p => p.length > 0);
        if (phones.length === 0) {
            message.warning(t('delivery.personnel.message.inputAccountRequired'));
            return;
        }
        if (phones.length > 50) {
            message.warning(t('delivery.personnel.message.phoneCountExceeded'));
            return;
        }
        setCheckLoading(true);
        try {
            const result = await checkDeliveryDriverAccounts(phones);
            setCheckResult(result);
            if (result.validDrivers.length === 0) {
                message.warning(t('delivery.personnel.message.noValidAccounts'));
            }
        } catch (error: any) {
            showError(error);
        } finally {
            setCheckLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!checkResult || checkResult.validDrivers.length === 0) {
            message.warning(t('delivery.personnel.message.noValidAccounts'));
            return;
        }
        if (!banReason) {
            message.warning(t('delivery.personnel.message.banReasonRequired'));
            return;
        }
        if (!banType) {
            message.warning(t('delivery.personnel.message.banTypeRequired'));
            return;
        }
        if (banType === 2 && !banPeriod) {
            message.warning(t('delivery.personnel.message.banPeriodRequired'));
            return;
        }
        setConfirmVisible(true);
    };

    const handleConfirm = async () => {
        setConfirmVisible(false);
        setSubmitLoading(true);
        try {
            const result = await setDriverBlacklist({
                phones: checkResult!.validDrivers.map(d => d.phone!),
                isBlacklist: true,
                banReason: banReason!,
                banType: banType!,
                banPeriod: banType === 2 ? banPeriod : null,
            });
            if (result.success) {
                message.success(t('delivery.personnel.message.banSuccess'));
                handleClose();
                onSuccess();
            } else {
                message.error(t(BLACKLIST_ERROR_I18N[result.message] ?? 'delivery.personnel.message.banFailed'));
            }
        } catch (error: any) {
            showError(error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const totalCount = checkResult
        ? checkResult.validDrivers.length + checkResult.invalidAccountIds.length
        : 0;
    const invalidCount = checkResult?.invalidAccountIds.length ?? 0;

    const validDriverColumns = [
        { title: t('delivery.personnel.label.driverPhone'), dataIndex: 'phone', key: 'phone', render: (v: number | null) => v != null ? String(v) : '-' },
        { title: t('delivery.personnel.label.region'), dataIndex: 'regionName', key: 'regionName', render: (v: string | null) => v || '-' },
        { title: t('delivery.personnel.label.warehouse'), dataIndex: 'warehouseName', key: 'warehouseName', render: (v: string | null) => v || '-' },
        { title: t('delivery.personnel.label.dsp'), dataIndex: 'dspName', key: 'dspName', render: (v: string | null) => v || '-' },
        {
            title: t('delivery.personnel.label.hasActiveDelivery'),
            dataIndex: 'hasActiveDelivery',
            key: 'hasActiveDelivery',
            render: (v: boolean) => v ? t('delivery.personnel.label.hasActiveDeliveryYes') : t('delivery.personnel.label.hasActiveDeliveryNo'),
        },
    ];

    const validDriverData = checkResult?.validDrivers.map((d, idx) => ({ key: idx, ...d })) || [];

    return (
        <>
            <Drawer
                title={
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span className="text-xl font-medium">{t('delivery.personnel.label.banDriver')}</span>
                    </div>
                }
                open={visible}
                onClose={handleClose}
                width={700}
                destroyOnClose
                footer={
                    checkResult && checkResult.validDrivers.length > 0 ? (
                        <div className="flex justify-end gap-2">
                            <Button onClick={handleClose}>{t('components.button.cancel')}</Button>
                            <Button type="primary" loading={submitLoading} onClick={handleSubmit}>
                                {t('delivery.personnel.label.confirmBan')}
                            </Button>
                        </div>
                    ) : null
                }
            >
                <div className="space-y-4">
                    <div className="text-red-500 text-sm flex items-center gap-1">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-red-500 text-xs font-bold shrink-0">!</span>
                        {t('delivery.personnel.message.banWarning')}
                    </div>

                    <div>
                        <div className="text-sm font-medium mb-1">
                            <span className="text-red-500 mr-0.5">*</span>
                            {t('delivery.personnel.label.driverPhone')}
                        </div>
                        <TextArea
                            value={phoneInput}
                            onChange={e => setPhoneInput(e.target.value.replace(/[^0-9,]/g, ''))}
                            placeholder={t('delivery.personnel.message.inputAccountPlaceholder')}
                            rows={4}
                        />
                        <div className="flex justify-end mt-2">
                            <Button
                                type={checkResult ? 'default' : 'primary'}
                                loading={checkLoading}
                                onClick={handleCheckPhones}
                            >
                                {t('components.button.confirm')}
                            </Button>
                        </div>
                    </div>

                    {checkResult && (
                        <>
                            <div className="space-y-2">
                                <div className="text-red-500 text-sm">
                                    {t('delivery.personnel.message.checkSummary', {
                                        total: totalCount,
                                        valid: checkResult.validDrivers.length,
                                        invalid: invalidCount,
                                    })}
                                </div>
                                {checkResult.validDrivers.length > 0 && (
                                    <Table
                                        size="small"
                                        columns={validDriverColumns}
                                        dataSource={validDriverData}
                                        pagination={false}
                                        bordered
                                    />
                                )}
                                {checkResult.invalidAccountIds.length > 0 && (
                                    <div className="text-orange-500 text-xs">
                                        {t('delivery.personnel.label.invalidAccountIds')}: {checkResult.invalidAccountIds.join(', ')}
                                    </div>
                                )}
                            </div>

                            {checkResult.validDrivers.length > 0 && (
                                <>
                                    <div>
                                        <div className="text-sm font-medium mb-1">
                                            <span className="text-red-500 mr-0.5">*</span>
                                            {t('delivery.personnel.label.banReason')}
                                        </div>
                                        <Select
                                            value={banReason}
                                            onChange={v => setBanReason(v)}
                                            placeholder={t('delivery.personnel.message.selectBanReason')}
                                            className="w-full"
                                            options={BAN_REASON_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.labelKey) }))}
                                        />
                                    </div>

                                    <div>
                                        <div className="text-sm font-medium mb-1">
                                            <span className="text-red-500 mr-0.5">*</span>
                                            {t('delivery.personnel.label.banType')}
                                        </div>
                                        <Select
                                            value={banType}
                                            onChange={v => { setBanType(v); setBanPeriod(undefined); }}
                                            placeholder={t('delivery.personnel.message.selectBanType')}
                                            className="w-full"
                                            options={BAN_TYPE_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.labelKey) }))}
                                        />
                                    </div>

                                    <div>
                                        <div className="text-sm font-medium mb-1">
                                            <span className="text-red-500 mr-0.5">*</span>
                                            {t('delivery.personnel.label.banPeriod')}
                                        </div>
                                        <Select
                                            value={banPeriod}
                                            onChange={v => setBanPeriod(v)}
                                            placeholder={t('delivery.personnel.message.selectBanPeriod')}
                                            className="w-full"
                                            disabled={banType === 1}
                                            options={BAN_PERIOD_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.labelKey) }))}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </Drawer>

            <Modal
                title={t('delivery.personnel.message.banConfirm')}
                open={confirmVisible}
                onOk={handleConfirm}
                onCancel={() => setConfirmVisible(false)}
                okText={t('components.button.confirm')}
                cancelText={t('components.button.cancel')}
                okButtonProps={{ danger: true }}
                zIndex={1010}
            />
        </>
    );
};

export default DriverBlacklistModal;
